import { useQuery } from "convex/react";

import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

interface MatterFiltersProps {
	connectionId: Id<"oauthConnections">;
	responsibleAttorneyClioId: number | null;
	practiceAreaClioId: number | null;
	onChangeAttorney: (id: number | null) => void;
	onChangePracticeArea: (id: number | null) => void;
}

type PickerOption = { clioId: number; name: string };

const ALL_ATTORNEY: PickerOption = { clioId: -1, name: "All attorneys" };
const ALL_PRACTICE: PickerOption = { clioId: -1, name: "All practice areas" };

export function MatterFilters({
	connectionId,
	responsibleAttorneyClioId,
	practiceAreaClioId,
	onChangeAttorney,
	onChangePracticeArea,
}: MatterFiltersProps) {
	const users = useQuery(api.clio.queries.listClioUsersForPicker, {
		connectionId,
	});
	const practiceAreas = useQuery(
		api.clio.queries.listClioPracticeAreasForPicker,
		{ connectionId },
	);

	const attorneyItems: PickerOption[] = [
		ALL_ATTORNEY,
		...(users ?? [])
			.filter((u) => u.enabled)
			.map((u) => ({ clioId: u.clioId, name: u.name })),
	];
	const practiceItems: PickerOption[] = [
		ALL_PRACTICE,
		...(practiceAreas ?? []).map((p) => ({ clioId: p.clioId, name: p.name })),
	];

	const selectedAttorney =
		attorneyItems.find((a) => a.clioId === responsibleAttorneyClioId) ??
		ALL_ATTORNEY;
	const selectedPractice =
		practiceItems.find((p) => p.clioId === practiceAreaClioId) ?? ALL_PRACTICE;

	return (
		<div className="flex flex-col gap-3 p-3">
			<div className="flex flex-col gap-1.5">
				<Label className="text-xs text-muted-foreground">
					Responsible attorney
				</Label>
				<Combobox
					items={attorneyItems}
					value={selectedAttorney}
					onValueChange={(next: PickerOption | null) => {
						if (!next || next.clioId === -1) onChangeAttorney(null);
						else onChangeAttorney(next.clioId);
					}}
					itemToStringLabel={(a: PickerOption) => a.name}
					itemToStringValue={(a: PickerOption) => String(a.clioId)}
				>
					<ComboboxInput
						aria-label="Filter by responsible attorney"
						placeholder="All attorneys"
					/>
					<ComboboxContent>
						<ComboboxEmpty>No matching attorneys.</ComboboxEmpty>
						<ComboboxList>
							{(a: PickerOption) => (
								<ComboboxItem key={a.clioId} value={a}>
									{a.name}
								</ComboboxItem>
							)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
			</div>
			<div className="flex flex-col gap-1.5">
				<Label className="text-xs text-muted-foreground">Practice area</Label>
				<Combobox
					items={practiceItems}
					value={selectedPractice}
					onValueChange={(next: PickerOption | null) => {
						if (!next || next.clioId === -1) onChangePracticeArea(null);
						else onChangePracticeArea(next.clioId);
					}}
					itemToStringLabel={(p: PickerOption) => p.name}
					itemToStringValue={(p: PickerOption) => String(p.clioId)}
				>
					<ComboboxInput
						aria-label="Filter by practice area"
						placeholder="All practice areas"
					/>
					<ComboboxContent>
						<ComboboxEmpty>No matching practice areas.</ComboboxEmpty>
						<ComboboxList>
							{(p: PickerOption) => (
								<ComboboxItem key={p.clioId} value={p}>
									{p.name}
								</ComboboxItem>
							)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
			</div>
		</div>
	);
}
